// ============================================================================
// OMS Detail Test — a FREE-HAND, multi-line estimate builder.
//
// Where the "Quick Scenario" tab stands up one main case (one item, one route),
// this builds a whole estimate the way a real one is shaped: several lines, each
// sourced from a vendor + warehouse exactly as the cart-availability screen does,
// any additional charges, a custom payment ledger, then placed under a chosen
// config. Backed by /oms/test-scenarios/detail/.
//
// The route is NOT an input — it is inferred server-side from the chosen warehouse
// (an MD warehouse ⇒ stock/transfer; a supplier's ⇒ backorder/dropship). EXPRESS is
// an ORDER-level mode (the config dropdown), which lifts every supplier line to a
// dropship.
// ============================================================================
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchVendors, getVendor } from "../../../apis/vendor.api";
import {
  DetailChargeInput,
  DetailLineInput,
  DetailPaymentInput,
  DetailResult,
  ScenarioDocument,
  TestScenarioOptions,
  runDetailTest,
} from "./api";

const { Text, Paragraph, Title } = Typography;

const DOC_STATE_COLOUR: Record<string, string> = {
  created: "green",
  reconciled: "green",
  pending: "blue",
  submitted: "gold",
  failed: "red",
};

const STAGE_STATE_COLOUR: Record<string, string> = {
  completed: "green",
  issued: "blue",
  planned: "default",
  cancelled: "default",
  held: "orange",
  error: "red",
};

// Local row types carry a stable id so React keys survive add/remove.
interface LineRow extends DetailLineInput {
  _id: number;
}

// The slice of a Vendor / VendorWarehouse the pickers need. Loaded live from the same endpoints
// the cart-availability dashboard uses (GET /vendor/, GET /vendor/{id}).
interface VendorOpt {
  id: number;
  name: string;
}
interface WarehouseOpt {
  id: number;
  name: string;
  city?: string;
}
interface ChargeRow extends DetailChargeInput {
  _id: number;
}
interface PaymentRow extends DetailPaymentInput {
  _id: number;
}

let _seq = 1;
const nextId = () => _seq++;

interface Props {
  options: TestScenarioOptions;
}

const OmsDetailTest: React.FC<Props> = ({ options }) => {
  const detail = options.detail;
  const products = options.products ?? [];

  const defaultVariant = useMemo(
    () => products.find((p) => p.linked_to_zoho)?.id ?? products[0]?.id,
    [products]
  );
  const mdWarehouseIds = useMemo(
    () => new Set(detail?.md_warehouse_ids ?? []),
    [detail]
  );

  // A new line starts as the seeded stock line (MD warehouse + its owning vendor). The route is
  // whatever that warehouse implies — there is no route to pick.
  const newLine = useCallback((): LineRow => {
    const d = detail?.default_line;
    return {
      _id: nextId(),
      variant_id: defaultVariant,
      qty: 3,
      vendor_id: d?.vendor_id,
      warehouse_id: d?.warehouse_id,
      lead_time: d?.lead_time ?? 0,
    };
  }, [detail, defaultVariant]);

  const [lines, setLines] = useState<LineRow[]>([newLine()]);
  const [charges, setCharges] = useState<ChargeRow[]>([]);
  const [payments, setPayments] = useState<PaymentRow[]>([]);

  // Vendors (loaded once) + a per-vendor warehouse cache, exactly like the cart dashboard: the
  // warehouse list for a line comes from GET /vendor/{selected vendor}.
  const [vendors, setVendors] = useState<VendorOpt[]>([]);
  const [whByVendor, setWhByVendor] = useState<Record<number, WarehouseOpt[]>>({});

  useEffect(() => {
    let alive = true;
    fetchVendors({}).then((rows: any[]) => {
      if (alive)
        setVendors(
          (rows ?? []).map((v) => ({ id: v.id, name: v.name || `Vendor ${v.id}` }))
        );
    });
    return () => {
      alive = false;
    };
  }, []);

  // Load (and cache) a vendor's warehouses. Returns them so callers can auto-select a sole WH.
  const loadWarehouses = useCallback(
    async (vendorId: number): Promise<WarehouseOpt[]> => {
      if (whByVendor[vendorId]) return whByVendor[vendorId];
      const data: any = await getVendor(vendorId);
      const whs: WarehouseOpt[] = (data?.warehouses ?? []).map((w: any) => ({
        id: w.id,
        name: w.name || w.vendor_godown_name || `WH ${w.id}`,
        city: w.city,
      }));
      setWhByVendor((m) => ({ ...m, [vendorId]: whs }));
      return whs;
    },
    [whByVendor]
  );

  // Warm the cache for whichever vendors the current lines already reference (the seeded default).
  useEffect(() => {
    lines.forEach((l) => {
      if (l.vendor_id != null && !whByVendor[l.vendor_id]) loadWarehouses(l.vendor_id);
    });
  }, [lines, whByVendor, loadWarehouses]);

  const [mode, setMode] = useState<string>("regular");
  const [paymentTerms, setPaymentTerms] = useState<string>(
    detail?.payment_terms?.[0]?.key ?? "prepaid"
  );
  const [deliveryKey, setDeliveryKey] = useState<string | undefined>(
    options.delivery_locations?.[0]?.key
  );
  const [autoPay, setAutoPay] = useState<boolean>(true);
  // When the customer placed the order. Blank = now. Written only to the estimate — every OMS clock
  // (stage SLA anchor, the 4 PM express cutoff, the 23:00 night-procurement window, the dates that
  // ride onto the Loginext booking) reads placement from there, so this is how those get tested.
  const [placedAt, setPlacedAt] = useState<Dayjs | null>(null);

  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<DetailResult | undefined>();
  const [error, setError] = useState<string | undefined>();

  const patchLine = (id: number, patch: Partial<LineRow>) =>
    setLines((ls) => ls.map((l) => (l._id === id ? { ...l, ...patch } : l)));

  // Picking a vendor clears the warehouse, loads that vendor's warehouses, and auto-selects the
  // sole one if there is exactly one — same behaviour as the cart-availability form.
  const onVendorChange = async (id: number, vendorId: number | undefined) => {
    patchLine(id, { vendor_id: vendorId ?? null, warehouse_id: undefined });
    if (vendorId == null) return;
    const whs = await loadWarehouses(vendorId);
    if (whs.length === 1) patchLine(id, { warehouse_id: whs[0].id });
  };

  // The route is INFERRED, shown read-only: an MD warehouse ships from our own stock (stock /
  // transfer); any other warehouse is a supplier's, so we procure (backorder, or dropship if
  // express / unserviced).
  const routeHint = (l: LineRow): string => {
    // Blank is blank — the builder stamps NULLs rather than defaulting, so a line with no lead time
    // is placed UNSOURCED and waits on OMS → Availability. That is the production shape, and no
    // route is decided until the desk picks the warehouse.
    if (l.lead_time == null) return "Awaiting availability (unsourced — no route yet)";
    if (l.warehouse_id == null) return "—";
    const base = mdWarehouseIds.has(l.warehouse_id)
      ? "Stock / transfer (MD warehouse)"
      : mode === "express"
      ? "Dropship (supplier ships direct — express)"
      : "Backorder / dropship (supplier)";
    // Partial availability: only `available_qty` takes this route now; the rest waits on the desk.
    if (l.available_qty != null && l.available_qty < Number(l.qty)) {
      return `${base} — partial: ${l.available_qty} of ${l.qty} now, rest pending`;
    }
    return base;
  };

  // EXPRESS is order-level only now — the mode dropdown, not any per-line flag.
  const willBeExpress = mode === "express";

  const productOptions = products.map((p) => ({
    value: p.id,
    label: `${p.sku}${p.linked_to_zoho ? "" : "  (new in Zoho)"}`,
  }));

  const onBuild = async () => {
    setRunning(true);
    setError(undefined);
    setResult(undefined);
    try {
      const data = await runDetailTest({
        items: lines.map((l) => ({
          variant_id: l.variant_id,
          qty: l.qty,
          vendor_id: l.vendor_id ?? null,
          warehouse_id: l.warehouse_id,
          lead_time: l.lead_time,
          available_qty: l.available_qty,
        })),
        additional_charges: charges.map(({ _id, ...c }) => c),
        payments: payments.map(({ _id, ...p }) => p),
        config: {
          mode,
          payment_terms: paymentTerms,
          auto_pay_balance: autoPay,
          delivery_key: deliveryKey,
          // Naive local string — the backend reads a naive value as IST, which is the timezone
          // every cutoff it feeds is expressed in.
          order_placed_time: placedAt ? placedAt.format("YYYY-MM-DDTHH:mm:ss") : undefined,
        },
      });
      setResult(data);
      message.success(
        `Created SalesOrder ${data.sales_order_id} (${data.line_count} line(s))`
      );
    } catch (e: any) {
      const detailMsg =
        e?.response?.data?.detail ??
        e?.message ??
        "Detail test failed for an unknown reason";
      setError(String(detailMsg));
    } finally {
      setRunning(false);
    }
  };

  if (!detail) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Detail builder unavailable"
        description="The server did not return detail-builder options. Update the OMS backend to the version that exposes /oms/test-scenarios/ → detail."
      />
    );
  }

  const docColumns = [
    { title: "Document", dataIndex: "type", key: "type" },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      render: (s: string) => <Tag color={DOC_STATE_COLOUR[s] ?? "default"}>{s}</Tag>,
    },
    {
      title: "Zoho id",
      dataIndex: "zoho_doc_id",
      key: "zoho_doc_id",
      render: (v: string | null) => v ?? "—",
    },
  ];

  return (
    <Row gutter={16}>
      <Col xs={24} lg={13}>
        {/* ---- Lines ------------------------------------------------------ */}
        <Card
          size="small"
          title="Lines"
          extra={
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={() => setLines((ls) => [...ls, newLine()])}
            >
              Add line
            </Button>
          }
        >
          <Paragraph type="secondary" style={{ fontSize: 12 }}>
            Pick a vendor and one of its warehouses per line — the same choice as the cart
            availability screen. The route is <Text strong>inferred</Text> from the warehouse: an
            MD warehouse (marked <Text code>★</Text>) ships from our own stock (stock / transfer);
            any other is a supplier's, so we procure (backorder, or dropship when express).
          </Paragraph>
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            {lines.map((l, idx) => (
              <Card
                key={l._id}
                size="small"
                type="inner"
                title={`Line ${idx + 1}`}
                extra={
                  lines.length > 1 ? (
                    <Button
                      size="small"
                      danger
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() =>
                        setLines((ls) => ls.filter((x) => x._id !== l._id))
                      }
                    />
                  ) : null
                }
              >
                <Row gutter={8}>
                  <Col span={16}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Product
                    </Text>
                    <Select
                      style={{ width: "100%" }}
                      size="small"
                      showSearch
                      optionFilterProp="label"
                      value={l.variant_id}
                      onChange={(v) => patchLine(l._id, { variant_id: v })}
                      options={productOptions}
                    />
                  </Col>
                  <Col span={8}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Qty
                    </Text>
                    <InputNumber
                      style={{ width: "100%" }}
                      size="small"
                      min={1}
                      value={Number(l.qty)}
                      onChange={(v) => patchLine(l._id, { qty: Number(v) || 1 })}
                    />
                  </Col>
                </Row>
                <Row gutter={8} style={{ marginTop: 8 }}>
                  <Col span={10}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Vendor / supplier
                    </Text>
                    <Select
                      style={{ width: "100%" }}
                      size="small"
                      showSearch
                      allowClear
                      optionFilterProp="label"
                      placeholder="select vendor"
                      value={l.vendor_id ?? undefined}
                      onChange={(v) => onVendorChange(l._id, v)}
                      options={vendors.map((v) => ({ value: v.id, label: v.name }))}
                    />
                  </Col>
                  <Col span={9}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Warehouse
                    </Text>
                    <Select
                      style={{ width: "100%" }}
                      size="small"
                      showSearch
                      optionFilterProp="label"
                      placeholder={l.vendor_id == null ? "pick vendor first" : "select warehouse"}
                      disabled={l.vendor_id == null}
                      value={l.warehouse_id}
                      onChange={(v) => patchLine(l._id, { warehouse_id: v })}
                      options={(whByVendor[l.vendor_id ?? -1] ?? []).map((w) => ({
                        value: w.id,
                        label:
                          (mdWarehouseIds.has(w.id) ? "★ " : "") +
                          w.name +
                          (w.city ? ` · ${w.city}` : ""),
                      }))}
                    />
                  </Col>
                  <Col span={5}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Lead time
                    </Text>
                    <InputNumber
                      style={{ width: "100%" }}
                      size="small"
                      min={0}
                      placeholder="days"
                      value={l.lead_time}
                      onChange={(v) =>
                        patchLine(l._id, { lead_time: v == null ? undefined : Number(v) })
                      }
                    />
                  </Col>
                </Row>
                <Row gutter={8} style={{ marginTop: 8 }}>
                  <Col span={10}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Available now (optional)
                    </Text>
                    <InputNumber
                      style={{ width: "100%" }}
                      size="small"
                      min={0}
                      max={Number(l.qty)}
                      placeholder="qty in stock now"
                      disabled={l.lead_time == null}
                      value={l.available_qty}
                      onChange={(v) =>
                        patchLine(l._id, { available_qty: v == null ? undefined : Number(v) })
                      }
                    />
                  </Col>
                  <Col span={14} style={{ display: "flex", alignItems: "flex-end" }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Set below qty to source part now (from the sourcing above) and leave the rest
                      pending on the availability desk. Needs a lead time.
                    </Text>
                  </Col>
                </Row>
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Route (inferred):{" "}
                  </Text>
                  <Tag
                    color={
                      l.lead_time == null
                        ? "orange"
                        : mdWarehouseIds.has(l.warehouse_id ?? -1)
                        ? "blue"
                        : "gold"
                    }
                  >
                    {routeHint(l)}
                  </Tag>
                </div>
              </Card>
            ))}
          </Space>
        </Card>

        {/* ---- Additional charges ---------------------------------------- */}
        <Card
          size="small"
          title="Additional charges"
          style={{ marginTop: 16 }}
          extra={
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={() =>
                setCharges((cs) => [
                  ...cs,
                  {
                    _id: nextId(),
                    charge_type: detail.charge_types[0]?.key ?? "transport charge",
                    amount: 500,
                    tax_value: 18,
                    tax_type: "cgst_sgst",
                  },
                ])
              }
            >
              Add charge
            </Button>
          }
        >
          {charges.length === 0 ? (
            <Text type="secondary">None.</Text>
          ) : (
            <Space direction="vertical" style={{ width: "100%" }} size="small">
              {charges.map((c) => (
                <Row gutter={8} key={c._id} align="middle">
                  <Col span={10}>
                    <Select
                      style={{ width: "100%" }}
                      size="small"
                      value={c.charge_type}
                      onChange={(v) =>
                        setCharges((cs) =>
                          cs.map((x) =>
                            x._id === c._id ? { ...x, charge_type: v } : x
                          )
                        )
                      }
                      options={detail.charge_types.map((t) => ({
                        value: t.key,
                        label: t.label,
                      }))}
                    />
                  </Col>
                  <Col span={6}>
                    <InputNumber
                      style={{ width: "100%" }}
                      size="small"
                      min={0}
                      prefix="₹"
                      value={Number(c.amount)}
                      onChange={(v) =>
                        setCharges((cs) =>
                          cs.map((x) =>
                            x._id === c._id ? { ...x, amount: Number(v) || 0 } : x
                          )
                        )
                      }
                    />
                  </Col>
                  <Col span={5}>
                    <InputNumber
                      style={{ width: "100%" }}
                      size="small"
                      min={0}
                      addonAfter="%"
                      value={Number(c.tax_value)}
                      onChange={(v) =>
                        setCharges((cs) =>
                          cs.map((x) =>
                            x._id === c._id ? { ...x, tax_value: Number(v) || 0 } : x
                          )
                        )
                      }
                    />
                  </Col>
                  <Col span={3}>
                    <Button
                      size="small"
                      danger
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() =>
                        setCharges((cs) => cs.filter((x) => x._id !== c._id))
                      }
                    />
                  </Col>
                </Row>
              ))}
            </Space>
          )}
        </Card>

        {/* ---- Payments -------------------------------------------------- */}
        <Card
          size="small"
          title="Payments"
          style={{ marginTop: 16 }}
          extra={
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={() =>
                setPayments((ps) => [
                  ...ps,
                  { _id: nextId(), amount_rupees: 1000, push_to_zoho: true },
                ])
              }
            >
              Add payment
            </Button>
          }
        >
          <Paragraph type="secondary" style={{ fontSize: 12 }}>
            Explicit EstimatePaymentTransaction rows. With <Text code>auto-pay
            balance</Text> on, the order is also topped up to the full payable so a
            prepaid order clears admission — leave payments empty for the common case.
          </Paragraph>
          {payments.length === 0 ? (
            <Text type="secondary">None{autoPay ? " (auto-pay will cover the payable)" : ""}.</Text>
          ) : (
            <Space direction="vertical" style={{ width: "100%" }} size="small">
              {payments.map((p) => (
                <Row gutter={8} key={p._id} align="middle">
                  <Col span={12}>
                    <InputNumber
                      style={{ width: "100%" }}
                      size="small"
                      min={1}
                      prefix="₹"
                      value={Number(p.amount_rupees)}
                      onChange={(v) =>
                        setPayments((ps) =>
                          ps.map((x) =>
                            x._id === p._id
                              ? { ...x, amount_rupees: Number(v) || 0 }
                              : x
                          )
                        )
                      }
                    />
                  </Col>
                  <Col span={9}>
                    <Switch
                      size="small"
                      checked={p.push_to_zoho !== false}
                      onChange={(v) =>
                        setPayments((ps) =>
                          ps.map((x) =>
                            x._id === p._id ? { ...x, push_to_zoho: v } : x
                          )
                        )
                      }
                    />{" "}
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      push Zoho advance
                    </Text>
                  </Col>
                  <Col span={3}>
                    <Button
                      size="small"
                      danger
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() =>
                        setPayments((ps) => ps.filter((x) => x._id !== p._id))
                      }
                    />
                  </Col>
                </Row>
              ))}
            </Space>
          )}
        </Card>

        {/* ---- Config + build ------------------------------------------- */}
        <Card size="small" title="Order config" style={{ marginTop: 16 }}>
          <Row gutter={8}>
            <Col span={12}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Mode
              </Text>
              <Select
                style={{ width: "100%" }}
                size="small"
                value={mode}
                onChange={setMode}
                options={detail.modes.map((m) => ({ value: m.key, label: m.label }))}
              />
            </Col>
            <Col span={12}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Payment terms
              </Text>
              <Select
                style={{ width: "100%" }}
                size="small"
                value={paymentTerms}
                onChange={setPaymentTerms}
                options={detail.payment_terms.map((t) => ({
                  value: t.key,
                  label: t.label,
                }))}
              />
            </Col>
          </Row>
          <Row gutter={8} style={{ marginTop: 8 }}>
            <Col span={12}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Deliver to
              </Text>
              <Select
                style={{ width: "100%" }}
                size="small"
                allowClear
                placeholder="seeded default"
                value={deliveryKey}
                onChange={setDeliveryKey}
                options={(options.delivery_locations ?? []).map((d) => ({
                  value: d.key,
                  label: d.label,
                }))}
              />
            </Col>
            <Col span={12} style={{ display: "flex", alignItems: "flex-end" }}>
              <Switch
                size="small"
                checked={autoPay}
                onChange={setAutoPay}
                style={{ marginRight: 8 }}
              />
              <Text style={{ fontSize: 12 }}>Auto-pay balance</Text>
            </Col>
          </Row>
          <Row gutter={8} style={{ marginTop: 8 }}>
            <Col span={12}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Order placed at (IST)
              </Text>
              <DatePicker
                style={{ width: "100%" }}
                size="small"
                allowClear
                showTime={{ format: "HH:mm" }}
                format="DD MMM YYYY HH:mm"
                placeholder="now"
                value={placedAt}
                onChange={setPlacedAt}
                presets={[
                  { label: "Now", value: dayjs() },
                  { label: "Today 15:00 (before express cutoff)", value: dayjs().hour(15).minute(0) },
                  { label: "Today 17:00 (express → next day)", value: dayjs().hour(17).minute(0) },
                  { label: "Yesterday 17:00", value: dayjs().subtract(1, "day").hour(17).minute(0) },
                ]}
              />
            </Col>
            <Col span={12} style={{ display: "flex", alignItems: "flex-end" }}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Stamped on the estimate only. Moves the SLA anchor, the 4 PM express promise and the
                night-procurement window together.
              </Text>
            </Col>
          </Row>

          {willBeExpress ? (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 12 }}
              message="This order will be placed EXPRESS — every supplier-sourced line becomes a dropship."
            />
          ) : null}

          <Button
            type="primary"
            block
            style={{ marginTop: 12 }}
            loading={running}
            onClick={onBuild}
            disabled={lines.length === 0}
          >
            {running ? "Building order…" : "Build & place order"}
          </Button>
        </Card>
      </Col>

      {/* ---- Result ------------------------------------------------------ */}
      <Col xs={24} lg={11}>
        {error ? (
          <Alert
            type="error"
            showIcon
            message="Detail test failed"
            description={
              <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{error}</pre>
            }
            style={{ marginBottom: 16 }}
          />
        ) : null}

        {result ? (
          <Card
            size="small"
            title={`SalesOrder ${result.sales_order_id}`}
            extra={<Link to={`/oms/${result.sales_order_id}`}>Open in OMS</Link>}
          >
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Estimate">
                {result.estimate_id}
              </Descriptions.Item>
              <Descriptions.Item label="Ship to">
                {result.ship_pincode ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Lines">
                {result.line_count}
              </Descriptions.Item>
              <Descriptions.Item label="Charges">
                {result.charge_count}
              </Descriptions.Item>
              <Descriptions.Item label="Mode">{result.mode}</Descriptions.Item>
              <Descriptions.Item label="Terms">
                {result.payment_terms}
              </Descriptions.Item>
              <Descriptions.Item label="Payable">
                ₹{result.payment.payable_rupees}
              </Descriptions.Item>
              <Descriptions.Item label="Paid">
                ₹{result.payment.paid_rupees}
              </Descriptions.Item>
            </Descriptions>

            {result.notes?.length ? (
              <Alert
                type="info"
                style={{ marginTop: 12 }}
                message={result.notes.join(" · ")}
              />
            ) : null}

            <Divider orientation="left" style={{ marginTop: 16 }}>
              Allocations
            </Divider>
            {result.allocations.map((a) => (
              <div key={a.id} style={{ marginBottom: 12 }}>
                <Space wrap>
                  <Text strong>#{a.id}</Text>
                  <Tag>{a.shipment_type}</Tag>
                  {a.stages.map((s) => (
                    <Tag
                      key={s.id}
                      color={STAGE_STATE_COLOUR[s.state] ?? "default"}
                      title={s.error ?? s.hold_reason ?? undefined}
                    >
                      {s.type}: {s.state}
                    </Tag>
                  ))}
                </Space>
                {a.stages
                  .filter((s) => s.error || s.hold_reason)
                  .map((s) => (
                    <Alert
                      key={`err-${s.id}`}
                      type={s.error ? "error" : "warning"}
                      style={{ marginTop: 6 }}
                      message={`${s.type}: ${s.error ?? s.hold_reason}`}
                    />
                  ))}
              </div>
            ))}

            <Divider orientation="left">Documents</Divider>
            <Table<ScenarioDocument>
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={result.documents}
              columns={docColumns}
            />
          </Card>
        ) : !error ? (
          <Card size="small">
            <Text type="secondary">
              Compose an estimate on the left and place it — the resulting sales
              order, its allocations (one route per line) and Zoho documents show up
              here.
            </Text>
          </Card>
        ) : null}
      </Col>
    </Row>
  );
};

export default OmsDetailTest;
